/**
 * The Studio app contract — the value object for an agent-built app (small
 * HTML/CSS/JS apps the user previews in a sandboxed iframe, saves to the
 * Studio library, and share-gates behind a paid plan).
 *
 * The GCS `studio/` prefix is the sole source of truth (path-as-identity,
 * spec R3): `appDir` IS the identity, and everything display-adjacent
 * (share state, "Updated …" labels) is derived by consumers from this shape
 * plus the share row — there are no denormalized display fields here.
 *
 * Pure module — domain-internal imports only. Safe for the Convex bundle,
 * frontend bundles, the CF worker, and unit tests.
 */

import { err, ok, type Result } from "./shared/result";

/** User-facing feature name — swap here to try "Workshop" / "The Dock". */
export const STUDIO_NAME = "Studio";

/** A fully-read app (spec §4.2 `readApp`): identity + display name + every
 *  file body. `updatedAt` is the max object mtime across the app's files
 *  (ms epoch; spec R4 — mtime authoritative), null when unknown. */
export interface StudioApp {
  /** Directory name under the vault's `studio/` prefix — the app's identity. */
  appDir: string;
  /** Display name: entry `<title>` when present, else Title-Cased appDir
   *  (`deriveAppDisplayName`). Display-only; `appDir` remains identity. */
  name: string;
  /** Entry file within `files` (`STUDIO_ENTRY_FILE`). */
  entry: string;
  /** File bodies keyed by app-relative path. */
  files: Record<string, string>;
  updatedAt: number | null;
}

// Everything below is pure — importable from the Convex bundle, the browser
// bundle, and the CF worker.

/** The app's entry file — its presence is what makes a directory an app. */
export const STUDIO_ENTRY_FILE = "index.html";

/** Per-file byte cap (spec R6, raised 2026-07-30). */
export const STUDIO_MAX_FILE_BYTES = 5 * 1024 * 1024; // 5 MiB

/**
 * Whole-app byte cap across all files (spec R6, raised 2026-07-30).
 * readApp returns every file body in one Convex action result, so this must
 * stay comfortably under Convex's 16 MiB function-return limit.
 */
export const STUDIO_MAX_APP_BYTES = 12 * 1024 * 1024; // 12 MiB

/** Max files per app (spec R6). */
export const STUDIO_MAX_FILES = 32;

/** Max apps per user — enforced at publish/list (spec R6). */
export const STUDIO_MAX_APPS = 50;

export type StudioAppBudgetError =
  | { kind: "missing_entry" }
  | { kind: "file_too_large"; file: string; sizeBytes: number }
  | { kind: "app_too_large"; totalBytes: number }
  | { kind: "too_many_files"; fileCount: number };

/**
 * Validate an app's file inventory against the R6 caps. Sizes-as-input: the
 * caller (action layer) probes object sizes and passes them here — this
 * module never touches storage. First violation wins, checked in order:
 * entry presence → file count → per-file cap → total cap.
 */
export function validateStudioAppBudget({
  fileSizes,
}: {
  fileSizes: Record<string, number>;
}): Result<true, StudioAppBudgetError> {
  const entries = Object.entries(fileSizes);

  if (!(STUDIO_ENTRY_FILE in fileSizes)) {
    return err({ kind: "missing_entry" });
  }
  if (entries.length > STUDIO_MAX_FILES) {
    return err({ kind: "too_many_files", fileCount: entries.length });
  }
  for (const [file, sizeBytes] of entries) {
    if (sizeBytes > STUDIO_MAX_FILE_BYTES) {
      return err({ kind: "file_too_large", file, sizeBytes });
    }
  }
  const totalBytes = entries.reduce((sum, [, size]) => sum + size, 0);
  if (totalBytes > STUDIO_MAX_APP_BYTES) {
    return err({ kind: "app_too_large", totalBytes });
  }
  return ok(true);
}

/**
 * Extension → Content-Type map for the serve path (spec §3.3). Text types
 * carry `; charset=utf-8`; binary types don't. Unknown extensions fall back
 * to octet-stream via `contentTypeForFile` (total function).
 */
export const STUDIO_CONTENT_TYPES: Record<string, string> = {
  html: "text/html; charset=utf-8",
  htm: "text/html; charset=utf-8",
  css: "text/css; charset=utf-8",
  js: "text/javascript; charset=utf-8",
  mjs: "text/javascript; charset=utf-8",
  json: "application/json; charset=utf-8",
  txt: "text/plain; charset=utf-8",
  md: "text/markdown; charset=utf-8",
  xml: "application/xml; charset=utf-8",
  csv: "text/csv; charset=utf-8",
  svg: "image/svg+xml",
  png: "image/png",
  jpg: "image/jpeg",
  jpeg: "image/jpeg",
  gif: "image/gif",
  webp: "image/webp",
  avif: "image/avif",
  ico: "image/x-icon",
  woff: "font/woff",
  woff2: "font/woff2",
  ttf: "font/ttf",
  otf: "font/otf",
  wasm: "application/wasm",
  mp3: "audio/mpeg",
  wav: "audio/wav",
  mp4: "video/mp4",
  webm: "video/webm",
  pdf: "application/pdf",
};

const OCTET_STREAM = "application/octet-stream";

/**
 * Total Content-Type resolver: lowercased final extension of the final path
 * segment, octet-stream fallback for unknown/absent extensions.
 */
export function contentTypeForFile({ path }: { path: string }): string {
  const lastSegment = path.split("/").pop() ?? "";
  const dot = lastSegment.lastIndexOf(".");
  if (dot <= 0) return OCTET_STREAM; // no extension, or dotfile-shaped
  const ext = lastSegment.slice(dot + 1).toLowerCase();
  return STUDIO_CONTENT_TYPES[ext] ?? OCTET_STREAM;
}

/**
 * Lenient `<title>` extraction from an HTML document. Tolerates attributes
 * on the tag, any case, surrounding whitespace/newlines. Returns null when
 * absent or empty — callers fall back to the appDir-derived name.
 */
export function extractHtmlTitle({ html }: { html: string }): string | null {
  const match = /<title[^>]*>([\s\S]*?)<\/title\s*>/i.exec(html);
  if (!match) return null;
  const title = match[1].replace(/\s+/g, " ").trim();
  return title.length > 0 ? title : null;
}

/**
 * Display name for an app: the entry's `<title>` when present, else the
 * kebab appDir Title-Cased (`kanban-board` → `Kanban Board`). Display-only;
 * the appDir remains identity (spec R3).
 */
export function deriveAppDisplayName({
  appDir,
  indexHtml,
}: {
  appDir: string;
  indexHtml?: string | null;
}): string {
  if (indexHtml != null) {
    const title = extractHtmlTitle({ html: indexHtml });
    if (title !== null) return title;
  }
  return appDir
    .split("-")
    .filter((word) => word.length > 0)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

const MINUTE_MS = 60 * 1000;
const HOUR_MS = 60 * MINUTE_MS;
const DAY_MS = 24 * HOUR_MS;

const MONTHS = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
] as const;

/**
 * Human "Updated …" label from a GCS mtime (spec R4: mtime authoritative).
 * `now` is injected — no `Date.now()` here (domain purity; tests pin the
 * derivation). Matches the mockup's label family ("Updated 2h ago",
 * "Updated yesterday", "Updated 3d ago").
 */
export function deriveUpdatedLabel({
  updatedAt,
  now,
}: {
  updatedAt: number;
  now: number;
}): string {
  const delta = now - updatedAt;
  if (delta < MINUTE_MS) return "Updated just now";
  if (delta < HOUR_MS) {
    return `Updated ${Math.floor(delta / MINUTE_MS)}m ago`;
  }
  if (delta < DAY_MS) {
    return `Updated ${Math.floor(delta / HOUR_MS)}h ago`;
  }
  if (delta < 2 * DAY_MS) return "Updated yesterday";
  if (delta < 7 * DAY_MS) {
    return `Updated ${Math.floor(delta / DAY_MS)}d ago`;
  }
  const d = new Date(updatedAt);
  return `Updated ${MONTHS[d.getUTCMonth()]} ${d.getUTCDate()}, ${d.getUTCFullYear()}`;
}

/**
 * Compose a srcdoc document for the sandboxed preview iframe (spec M13):
 * inline same-app relative stylesheet links (`<link rel="stylesheet"
 * href="./x.css">` → `<style>`) and script sources (`<script src="./x.js">`
 * → inline `<script>`) so multi-file apps render inside srcdoc, where
 * relative fetches have no origin. Absolute/CDN URLs (scheme or
 * protocol-relative) are left untouched; references to files missing from
 * the map are left untouched (lenient/total — never throws).
 */
export function composeSrcdoc({
  files,
  entry,
}: {
  files: Record<string, string>;
  entry: string;
}): string {
  const html = files[entry] ?? "";

  const lookup = (ref: string): string | undefined => {
    if (/^[a-z][a-z0-9+.-]*:/i.test(ref) || ref.startsWith("//")) {
      return undefined; // absolute / CDN / protocol-relative — untouched
    }
    if (ref.startsWith("/")) return undefined; // origin-absolute — untouched
    const key = ref.startsWith("./") ? ref.slice(2) : ref;
    return files[key];
  };

  const withStyles = html.replace(
    /<link\b[^>]*>/gi,
    (tag) => {
      if (!/\brel\s*=\s*["']?stylesheet["']?/i.test(tag)) return tag;
      const href = /\bhref\s*=\s*["']([^"']+)["']/i.exec(tag)?.[1];
      if (!href) return tag;
      const content = lookup(href);
      if (content === undefined) return tag;
      return `<style>\n${content}\n</style>`;
    },
  );

  return withStyles.replace(
    /<script\b([^>]*)>\s*<\/script\s*>/gi,
    (tag, attrs: string) => {
      const src = /\bsrc\s*=\s*["']([^"']+)["']/i.exec(attrs)?.[1];
      if (!src) return tag;
      const content = lookup(src);
      if (content === undefined) return tag;
      const isModule = /\btype\s*=\s*["']?module["']?/i.test(attrs);
      return `<script${isModule ? ' type="module"' : ""}>\n${content}\n</script>`;
    },
  );
}
